import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import GlobalErrorBanner from './GlobalErrorBanner';
import Tutorial, { openTutorial } from './Tutorial';
import {
  fetchSidebarCounts,
  type SidebarCounts,
} from './sidebarCounts';
import { clearSession, getSession, type StoredSession } from './session';
import './Layout.css';

/**
 * Nav sections mirror `assets/Project Sentinel/shared.js` (sidebar) plus the
 * public-site chrome entries from `shared.js`. All labels are the design's
 * strings so the port stays 1:1. Badges that were hard-coded previously
 * (`247`, `14`, `●1`) are now driven by `useQuery(['sidebar-counts'])`.
 */
interface NavEntry {
  key: string;
  label: string;
  to: string;
  /** Key on `SidebarCounts` whose value should render as a right-aligned badge. */
  countKey?: keyof SidebarCounts;
  /** When true, render the count as a `warn` pill (the `●1` style). */
  asWarn?: boolean;
}

interface NavSection {
  title: string;
  items: NavEntry[];
}

const SECTIONS: NavSection[] = [
  {
    title: 'Operations',
    items: [
      { key: 'overview', label: 'Overview', to: '/dashboard' },
      { key: 'replays', label: 'Replay sessions', to: '/replays', countKey: 'replays' },
      { key: 'scenarios', label: 'Scenario library', to: '/scenarios' },
      { key: 'divergence', label: 'Divergence map', to: '/divergence' },
      {
        key: 'audit-packages',
        label: 'Audit packages',
        to: '/audit-packages',
        countKey: 'auditPackages',
      },
    ],
  },
  {
    title: 'Detection',
    items: [
      { key: 'alerts', label: 'Live alerts feed', to: '/alerts' },
      { key: 'drift', label: 'Rule drift', to: '/rule-drift' },
      { key: 'models', label: 'Model registry', to: '/model-registry' },
    ],
  },
  {
    title: 'Governance',
    items: [
      {
        key: 'attestations',
        label: 'Attestations',
        to: '/attestations',
        countKey: 'attestationsPending',
        asWarn: true,
      },
      { key: 'keys', label: 'Quorum keys', to: '/quorum-keys' },
      { key: 'team', label: 'Team & roles', to: '/team' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { key: 'integrations', label: 'Integrations', to: '/integrations' },
      { key: 'coverage', label: 'Coverage', to: '/coverage' },
      { key: 'audit', label: 'Audit', to: '/audit' },
      { key: 'fieldnotes', label: 'Field notes', to: '/field-notes' },
      { key: 'settings', label: 'Settings', to: '/settings' },
    ],
  },
];

/**
 * Renders a sidebar badge value. While the query is loading we deliberately
 * render an em-dash rather than flashing `0` (which would read as "zero
 * replays / attestations" — misleading). When the query errors we still
 * render the dash rather than blowing up the chrome.
 */
function renderCount(count: number | undefined): string {
  return typeof count === 'number' ? String(count) : '—';
}

export default function Layout() {
  const countsQ = useQuery({
    queryKey: ['sidebar-counts'],
    queryFn: fetchSidebarCounts,
    staleTime: 60_000,
  });

  // Scroll to top on every route change so the operator never lands
  // mid-page after navigating from a long-scrolled screen (e.g. clicking
  // an item from a deep Replays scroll position should land on a fresh
  // Dashboard at y=0). Watches `pathname` only — not search/hash —
  // because the deck-stage's `#slide=N` hash already drives its own
  // intra-iframe scroll and shouldn't reset the parent.
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return (
    <div>
      <GlobalErrorBanner />
      <DashTop counts={countsQ.data} />
      <div className="dash-layout">
        <DashSide counts={countsQ.data} isLoading={countsQ.isLoading} />
        <main className="dash-main">
          <Outlet />
        </main>
      </div>
      <Tutorial />
    </div>
  );
}

function DashTop({ counts }: { counts: SidebarCounts | undefined }) {
  const attestations =
    typeof counts?.attestationsPending === 'number' ? counts.attestationsPending : null;
  return (
    <header className="dash-top" role="banner">
      <a href="/dashboard" className="dash-logo">
        <SentinelLogoMark />
        <span>SENTINEL</span>
        <span className="mono dash-logo-sub">CONSOLE</span>
      </a>
      <div className="dash-tenant" aria-label="Current tenant">
        <span className="dot" />
        <span>Northstar Bank · EU-Production</span>
        <span className="dash-tenant-caret mono">▾</span>
      </div>
      <GlobalSearch />
      <div className="dash-status mono" aria-label="System status" data-testid="dash-status">
        <span>
          <span style={{ color: '#6FCF97' }}>●</span> 12/12
        </span>
        <span>
          <span style={{ color: '#6FCF97' }}>●</span> 2.4M/s
        </span>
        <span>
          <span style={{ color: '#E5C25D' }}>●</span>{' '}
          {attestations === null
            ? '— PENDING'
            : `${attestations} PENDING`}
        </span>
      </div>
      <button
        type="button"
        className="tutorial-trigger"
        onClick={() => openTutorial()}
        data-testid="start-tutorial"
        title="Re-run the onboarding tutorial — walks through Dashboard, Scenarios, Replays, Divergence and Attestations."
        aria-label="Start tutorial"
      >
        Start tutorial
      </button>
      <SessionBadge />
    </header>
  );
}

function SessionBadge() {
  const [session, setSession] = useState<StoredSession | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    void getSession().then((s) => {
      if (!cancelled) setSession(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const handleLogout = async () => {
    await clearSession();
    navigate('/auth', { replace: true });
  };
  if (!session) {
    return (
      <div className="dash-user">
        <div className="dash-avatar">EV</div>
        <div className="dash-user-meta">
          <div>Elena Vasquez</div>
          <div>MLRO · Northstar</div>
        </div>
      </div>
    );
  }
  const initials = session.email.slice(0, 2).toUpperCase();
  return (
    <div className="dash-user" data-testid="dash-session">
      <div className="dash-avatar">{initials}</div>
      <div className="dash-user-meta">
        <div>{session.email}</div>
        <div>{session.role}</div>
      </div>
      <button
        type="button"
        className="dash-logout"
        onClick={() => void handleLogout()}
        data-testid="dash-logout"
        title="Sign out · clears the IndexedDB session and returns to /auth"
      >
        Sign out
      </button>
    </div>
  );
}

function DashSide({
  counts,
  isLoading,
}: {
  counts: SidebarCounts | undefined;
  isLoading: boolean;
}) {
  return (
    <aside className="dash-side" aria-label="Primary" data-testid="dash-side">
      {SECTIONS.map((sec) => (
        <div key={sec.title}>
          <div className="nav-sec">{sec.title}</div>
          {sec.items.map((item) => {
            const value = item.countKey ? counts?.[item.countKey] : undefined;
            const shouldRenderBadge = item.countKey !== undefined;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              >
                <span>{item.label}</span>
                {shouldRenderBadge && (
                  <span
                    className={item.asWarn ? 'nav-warn mono' : 'nav-badge mono'}
                    data-testid={`nav-badge-${item.key}`}
                    aria-label={
                      isLoading
                        ? `${item.label} count loading`
                        : `${item.label} count ${renderCount(value)}`
                    }
                  >
                    {item.asWarn && typeof value === 'number' && value > 0
                      ? `●${value}`
                      : renderCount(value)}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
      <div className="tenant-card">
        <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 8 }}>
          TENANT STATUS
        </div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>
          <strong>Q1 2026</strong> replay window
        </div>
        <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 10 }}>
          Sealed · FCA accepted
        </div>
        <div
          style={{
            height: 4,
            background: 'var(--slate-200)',
            borderRadius: 2,
            overflow: 'hidden',
            marginBottom: 6,
          }}
        >
          <div style={{ height: '100%', width: '78%', background: 'var(--navy)' }} />
        </div>
        <div
          className="mono"
          style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--slate-500)' }}
        >
          <span>Q2 · 78%</span>
          <span>21d left</span>
        </div>
      </div>
    </aside>
  );
}

function SentinelLogoMark() {
  return (
    <svg width="22" height="24" viewBox="0 0 30 32" fill="none" aria-hidden="true">
      <path
        d="M2 4 L15 1 L28 4 L28 17 C28 24.5 22.5 29.5 15 31 C7.5 29.5 2 24.5 2 17 Z"
        stroke="#9FC2DD"
        strokeWidth="1.4"
        fill="none"
      />
      <path
        d="M8 11 L15 8 L22 11 M8 17 L15 14 L22 17 M8 23 L15 20 L22 23"
        stroke="#9FC2DD"
        strokeWidth="1"
        strokeLinecap="square"
      />
      <circle cx="15" cy="8" r="1.4" fill="#9FC2DD" />
      <circle cx="15" cy="14" r="1.4" fill="#9FC2DD" />
      <circle cx="15" cy="20" r="1.4" fill="#9FC2DD" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <circle cx="5" cy="5" r="3.5" stroke="#8FA6B8" strokeWidth="1" />
      <path d="M8 8 L11 11" stroke="#8FA6B8" strokeWidth="1" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// GlobalSearch — top-bar command palette
//
// Minimum viable: hard-coded command list of routes + actions. Types filter
// the list; Enter picks the first match; ↑↓ navigate; ⌘K / Ctrl-K focuses
// the input from anywhere in the app.
// ---------------------------------------------------------------------------

interface SearchCommand {
  label: string;
  hint: string;
  keywords: string;
  to: string;
}

const SEARCH_COMMANDS: SearchCommand[] = [
  { label: 'Dashboard', hint: 'Validation overview', keywords: 'dashboard overview parity kpi', to: '/dashboard' },
  { label: 'Scenario library', hint: 'Browse + edit scenarios', keywords: 'scenarios catalogue typology rule', to: '/scenarios' },
  { label: 'Replay sessions', hint: 'Past + new replay runs', keywords: 'replays runs receipts sessions', to: '/replays' },
  { label: 'Alerts feed', hint: 'Historical + synthetic alerts', keywords: 'alerts triage disposition', to: '/alerts' },
  { label: 'Divergence map', hint: 'Matched / missed / extra / drift', keywords: 'divergence missed matched extra drift', to: '/divergence' },
  { label: 'Rule drift', hint: 'DriftScore per scenario', keywords: 'drift rule-drift score', to: '/rule-drift' },
  { label: 'Model registry', hint: 'Engine + model version pins', keywords: 'models registry version engine', to: '/model-registry' },
  { label: 'Quorum keys', hint: 'Signer set + rotations', keywords: 'quorum keys signers rotate', to: '/quorum-keys' },
  { label: 'Attestations', hint: 'Quorum-signed receipts', keywords: 'attestations receipts evidence sign', to: '/attestations' },
  { label: 'Audit packages', hint: 'Sealed + downloadable', keywords: 'audit packages export seal', to: '/audit-packages' },
  { label: 'Integrations', hint: 'Source adapter catalog', keywords: 'integrations adapters vendors fccm', to: '/integrations' },
  { label: 'Coverage', hint: 'Scenario × window matrix', keywords: 'coverage matrix scope', to: '/coverage' },
  { label: 'Audit trail', hint: 'Every operation, exportable', keywords: 'audit trail log events', to: '/audit' },
  { label: 'Field notes', hint: 'Writeups + postmortems', keywords: 'field notes writeups research', to: '/field-notes' },
  { label: 'Settings', hint: 'Data mode, tenant, retention', keywords: 'settings config tenant retention', to: '/settings' },
  { label: 'Team & roles', hint: 'Members + RBAC', keywords: 'team members roles invite', to: '/team' },
];

function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return SEARCH_COMMANDS;
    return SEARCH_COMMANDS.filter(
      (c) =>
        c.label.toLowerCase().includes(needle) ||
        c.hint.toLowerCase().includes(needle) ||
        c.keywords.includes(needle),
    );
  }, [q]);

  // ⌘K / Ctrl-K focus-from-anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pick = (to: string) => {
    setOpen(false);
    setQ('');
    navigate(to);
  };

  return (
    <div className="dash-search-wrap" data-testid="dash-search">
      <label className="dash-search">
        <SearchIcon />
        <input
          ref={inputRef}
          aria-label="Search replays, scenarios, receipts"
          placeholder="Search replays, scenarios, receipts…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(results.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const hit = results[active];
              if (hit) pick(hit.to);
            }
          }}
          data-testid="dash-search-input"
        />
        <span className="kbd">⌘ K</span>
      </label>
      {open && results.length > 0 && (
        <div
          className="dash-search-results"
          role="listbox"
          data-testid="dash-search-results"
        >
          {results.map((r, i) => (
            <button
              key={r.to}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`dash-search-result${i === active ? ' is-active' : ''}`}
              // mousedown (not click) fires before input's blur hides the list.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r.to);
              }}
              onMouseEnter={() => setActive(i)}
              data-testid={`dash-search-hit-${r.to.replace(/\//g, '')}`}
            >
              <span className="dash-search-result__label">{r.label}</span>
              <span className="dash-search-result__hint">{r.hint}</span>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && (
        <div className="dash-search-results dash-search-results--empty" role="listbox">
          <div className="dash-search-empty">
            No matches for &ldquo;{q}&rdquo;. Try <span className="mono">replays</span>,{' '}
            <span className="mono">attestations</span>, <span className="mono">drift</span>.
          </div>
        </div>
      )}
    </div>
  );
}
